import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Key, Wand2, Copy, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function ActivationCodesTab({
  activationCodes, newCodeForm, setNewCodeForm, onCreateCode,
  bulkGenForm, setBulkGenForm, generatingCodes, onGenerateBulk, lastGenerated,
  onDeleteCode
}) {
  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied to clipboard!'); };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Bulk Code Generator */}
      <Card className="md:col-span-2 border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-transparent">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-purple-500" />Bulk Code Generator</CardTitle>
          <CardDescription>Generate multiple activation codes at once</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onGenerateBulk} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2"><Label htmlFor="prefix">Prefix</Label><Input id="prefix" placeholder="EGNS2026" value={bulkGenForm.prefix} onChange={(e) => setBulkGenForm(prev => ({ ...prev, prefix: e.target.value }))} data-testid="bulk-prefix" /></div>
              <div className="space-y-2"><Label htmlFor="startNumber">Start Number</Label><Input id="startNumber" type="number" min="1" value={bulkGenForm.startNumber} onChange={(e) => setBulkGenForm(prev => ({ ...prev, startNumber: e.target.value }))} data-testid="bulk-start" /></div>
              <div className="space-y-2"><Label htmlFor="count">Count</Label><Input id="count" type="number" min="1" max="1000" value={bulkGenForm.count} onChange={(e) => setBulkGenForm(prev => ({ ...prev, count: e.target.value }))} data-testid="bulk-count" /></div>
              <div className="space-y-2"><Label htmlFor="usesAllowed">Uses Each</Label><Input id="usesAllowed" type="number" min="-1" value={bulkGenForm.usesAllowed} onChange={(e) => setBulkGenForm(prev => ({ ...prev, usesAllowed: e.target.value }))} data-testid="bulk-uses" /></div>
            </div>
            <div className="flex items-center gap-4">
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={generatingCodes} data-testid="generate-bulk-btn">
                {generatingCodes ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}Generate {bulkGenForm.count} Codes
              </Button>
              <p className="text-sm text-muted-foreground">Will create: <span className="font-mono text-purple-400">{bulkGenForm.prefix}-{bulkGenForm.startNumber}</span> to <span className="font-mono text-purple-400">{bulkGenForm.prefix}-{parseInt(bulkGenForm.startNumber) + parseInt(bulkGenForm.count) - 1}</span></p>
            </div>
            {lastGenerated && (
              <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-sm font-medium text-green-400 mb-2">Last Generated: {lastGenerated.created_count} codes</p>
                <div className="flex flex-wrap gap-2">
                  {lastGenerated.first_code && <div className="flex items-center gap-1"><span className="text-xs text-muted-foreground">First:</span><code className="text-xs bg-card px-2 py-1 rounded">{lastGenerated.first_code}</code><Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => copyToClipboard(lastGenerated.first_code)}><Copy className="h-3 w-3" /></Button></div>}
                  {lastGenerated.last_code && <div className="flex items-center gap-1"><span className="text-xs text-muted-foreground">Last:</span><code className="text-xs bg-card px-2 py-1 rounded">{lastGenerated.last_code}</code><Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => copyToClipboard(lastGenerated.last_code)}><Copy className="h-3 w-3" /></Button></div>}
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Create Single Code */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Key className="h-5 w-5 text-teal-500" />Create Single Code</CardTitle>
          <CardDescription>Create a custom activation code</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onCreateCode} className="space-y-4">
            <div className="space-y-2"><Label htmlFor="code">Code</Label><Input id="code" placeholder="e.g., VIP-STUDENT-2026" value={newCodeForm.code} onChange={(e) => setNewCodeForm(prev => ({ ...prev, code: e.target.value }))} data-testid="new-code-input" /></div>
            <div className="space-y-2"><Label htmlFor="uses">Uses Allowed</Label><Input id="uses" type="number" min="-1" placeholder="1" value={newCodeForm.uses} onChange={(e) => setNewCodeForm(prev => ({ ...prev, uses: e.target.value }))} data-testid="new-code-uses" /><p className="text-xs text-muted-foreground">Use -1 for unlimited uses</p></div>
            <Button type="submit" className="w-full" data-testid="create-code-btn">Create Code</Button>
          </form>
        </CardContent>
      </Card>

      {/* Existing Codes */}
      <Card>
        <CardHeader>
          <CardTitle>Existing Codes</CardTitle>
          <CardDescription>{activationCodes.length} activation codes total</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {activationCodes.slice(0, 50).map((c) => (
              <div key={c.code} className="flex items-center justify-between p-2 border rounded text-sm">
                <div><p className="font-mono font-medium">{c.code}</p><p className="text-xs text-muted-foreground">{c.uses_count}/{c.uses_allowed === -1 ? '\u221e' : c.uses_allowed} used</p></div>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 h-8" onClick={() => onDeleteCode(c.code)}><XCircle className="h-4 w-4" /></Button>
              </div>
            ))}
            {activationCodes.length > 50 && <p className="text-center text-sm text-muted-foreground py-2">+{activationCodes.length - 50} more codes</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
